import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Anchor, Lock, Loader2, ArrowLeft, CheckCircle2, XCircle, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BACKGROUND_IMAGE = 'https://images.pexels.com/photos/3808621/pexels-photo-3808621.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Password validation
  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    match: password === confirmPassword && password.length > 0
  };

  const allChecksPassed = Object.values(passwordChecks).every(Boolean);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('No reset token provided');
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.post(`${API_URL}/api/auth/verify-reset-token`, { token });
        setTokenValid(true);
        setTokenEmail(response.data.email);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired reset token');
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!allChecksPassed) {
      toast.error('Please meet all password requirements');
      return;
    }

    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    // Verifying token
    if (verifying) {
      return (
        <div className="flex flex-col items-center justify-center py-8 space-y-4">
          <Loader2 className="h-8 w-8 text-amber-400 animate-spin" />
          <p className="text-slate-400">Verifying reset token...</p>
        </div>
      );
    }

    // Token invalid or expired
    if (!tokenValid) {
      return (
        <div className="space-y-6">
          <div className="flex justify-center">
            <div className="p-4 bg-red-500/20 rounded-full">
              <XCircle className="h-12 w-12 text-red-400" />
            </div>
          </div>
          <div className="text-center space-y-2">
            <p className="text-slate-300 text-sm">{error}</p>
            <p className="text-slate-400 text-xs">
              Please request a new password reset link.
            </p>
          </div>
          <Link to="/forgot-password" className="block">
            <Button 
              className="w-full bg-amber-600 hover:bg-amber-700 h-11"
              data-testid="request-new-link-btn"
            >
              Request New Link
            </Button>
          </Link>
          <Link to="/login" className="block">
            <Button 
              variant="ghost"
              className="w-full text-slate-400 hover:text-slate-300 hover:bg-slate-700/50 h-11"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Sign In
            </Button>
          </Link>
        </div>
      );
    }

    // Password reset successful
    if (success) {
      return (
        <div className="space-y-6">
          <div className="flex justify-center">
            <div className="p-4 bg-amber-500/20 rounded-full">
              <CheckCircle2 className="h-12 w-12 text-amber-400" />
            </div>
          </div>
          <div className="text-center space-y-2">
            <p className="text-slate-300 text-sm">
              Your password has been reset successfully!
            </p>
            <p className="text-slate-400 text-xs">
              Redirecting to sign in...
            </p>
          </div>
          <Link to="/login" className="block">
            <Button 
              className="w-full bg-amber-600 hover:bg-amber-700 h-11"
              data-testid="go-to-login-btn"
            >
              Sign In Now
            </Button>
          </Link>
        </div>
      );
    }

    // Reset password form
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="text-center mb-4">
          <p className="text-slate-400 text-sm">
            Resetting password for <span className="text-white font-medium">{tokenEmail}</span>
          </p>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="password" className="text-slate-300 text-sm">New Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-10 pr-10 bg-slate-900/80 border-slate-600 text-white placeholder:text-slate-500 h-11"
              required
              data-testid="reset-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-400"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword" className="text-slate-300 text-sm">Confirm Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="pl-10 pr-10 bg-slate-900/80 border-slate-600 text-white placeholder:text-slate-500 h-11"
              required
              data-testid="reset-confirm-password-input"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-400"
            >
              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Password requirements */}
        <div className="bg-slate-900/60 rounded-lg p-3 space-y-1.5">
          <p className="text-xs text-slate-400 font-medium mb-2">Password requirements:</p>
          {[
            { key: 'length', label: 'At least 8 characters' },
            { key: 'uppercase', label: 'One uppercase letter' },
            { key: 'lowercase', label: 'One lowercase letter' },
            { key: 'number', label: 'One number' },
            { key: 'special', label: 'One special character' },
            { key: 'match', label: 'Passwords match' }
          ].map(({ key, label }) => (
            <div key={key} className="flex items-center gap-2">
              <div className={`h-1.5 w-1.5 rounded-full ${passwordChecks[key] ? 'bg-amber-400' : 'bg-slate-600'}`} />
              <span className={`text-xs ${passwordChecks[key] ? 'text-amber-400' : 'text-slate-500'}`}>
                {label}
              </span>
            </div>
          ))}
        </div>

        <Button 
          type="submit" 
          className="w-full bg-amber-600 hover:bg-amber-700 h-11 text-base font-medium shadow-lg shadow-amber-600/20"
          disabled={loading || !allChecksPassed}
          data-testid="reset-password-submit-btn"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Resetting...
            </>
          ) : (
            'Reset Password'
          )}
        </Button>
      </form>
    );
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${BACKGROUND_IMAGE})` }}
      />
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-[2px]" />
      
      {/* Content */}
      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Seafarer Logbook" className="h-16 w-16 mx-auto mb-4" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Seafarer Logbook</h1>
          <p className="text-slate-300 mt-1">Your trips, earnings & tax — all in one place</p>
        </div>

        <Card className="bg-slate-800/90 border-slate-700 backdrop-blur-sm shadow-2xl">
          <CardHeader className="pb-4">
            <CardTitle className="text-white text-lg sm:text-xl">
              {success ? 'Password Reset!' : 'Reset Password'}
            </CardTitle>
            <CardDescription className="text-slate-400 text-sm">
              {success 
                ? 'Your password has been updated successfully.'
                : 'Create a new password for your account.'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {renderContent()}
          </CardContent>
        </Card>
        
        {/* Footer */}
        <p className="text-center text-slate-500 text-xs mt-6">
          Track your qualifying days for UK tax relief
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
